import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getUser, updateUser, mapRoleLabelToApi, mapApiRoleToLabel } from '../../services/UserService';

const EditUserPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    department: '',
    mobile: '',
    email: '',
    company: '',
    role: '' as string,
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!id) return;
        const { user: u } = await getUser(id);
        if (!mounted) return;
        setFormData({
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          department: (u.department || '') as string,
          mobile: u.contactNumber || '',
          email: u.companyEmail || '',
          company: (u.company || '') as string,
          role: mapApiRoleToLabel(u.role)
        });
      } catch (e: any) {
        setError(e.message || 'Failed to load user');
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    setError('');
    if (!id) return;
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.mobile) {
      setError('Please fill in First Name, Last Name, Email and Mobile.');
      return;
    }
    setSaving(true);
    try {
      await updateUser(id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyEmail: formData.email,
        contactNumber: formData.mobile,
        department: formData.department,
        company: formData.company,
        role: mapRoleLabelToApi(formData.role)
      });
      navigate('/superadmin/users-roles', { replace: true, state: { userUpdated: true } });
    } catch (e: any) {
      setError(e.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-7xl mx-auto p-6">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-medium">Edit User</h1>
        <div className="space-x-3">
          <button
            onClick={() => navigate('/superadmin/users-roles')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 text-red-600 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        <div>
          <h2 className="text-lg font-medium mb-4">User Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number
              </label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Name
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-medium mb-4">User Role</h2>
          <div className="space-y-3">
            {['Super Admin', 'Admin', 'Manager', 'User', 'Demo'].map((role) => (
              <div key={role} className="flex items-center">
                <input
                  type="radio"
                  id={role}
                  name="role"
                  value={role}
                  checked={formData.role === role}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-[#FF5722] border-gray-300 focus:ring-[#FF5722]"
                />
                <label htmlFor={role} className="ml-2 text-sm text-gray-700">
                  {role}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUserPage;


